/**
 * build.ts — v0M7.3 (2026-07-25).
 *
 * Two sim systems for the world-buildable layer per `MAP.md` §9:
 *
 *   1. `buildSystem` — advances each Army's active build order per tick.
 *      When the Army's centre is within `WORLD_BUILD_ARRIVAL_EPSILON`
 *      cells of the target cell AND the Army is not in a battle,
 *      `progressDays` accrues `1/TICKS_PER_GAME_DAY` per tick. When the
 *      Army moves off (distance exceeds epsilon), `progressDays` resets
 *      to 0 next tick per the design lock — the order itself persists
 *      so the Army resumes fresh if it returns. On reaching the per-kind
 *      `MapStructureSpec.buildTimeDays` threshold, a new WorldStructure
 *      is minted at the target cell + the Army's build order is cleared.
 *
 *   2. `captureSystem` — runs at the day boundary. For every park-
 *      capturable WorldStructure (Watch Tower at v1 — kinds where
 *      `MapStructureSpec.captureDays > 0`), check whether an enemy Army
 *      is parked on the cell:
 *        - If yes + no existing captureProgress: start one with
 *          default mode='capture', byFactionId = enemy's faction, days=0.
 *        - If yes + existing captureProgress attributed to the same
 *          faction: increment days.
 *        - If yes + existing captureProgress attributed to a DIFFERENT
 *          faction: reset — new attacker takes over the timer at 0
 *          (design decision at v0M7; playtest may want to keep the
 *          incumbent instead).
 *        - If no enemy present: clear captureProgress (progress not
 *          persisted across gaps).
 *      On days >= captureDays: apply capture (flip ownerFactionId) or
 *      destroy (delete from state.worldStructures). Clear captureProgress.
 *
 * Determinism: iteration via `sortedIds(state.armies)` +
 * `sortedIds(state.worldStructures)`; captures apply after iteration
 * to avoid mutating during traversal. No RNG consumed at v0M7.3
 * (Watch Tower capture is a deterministic timer).
 *
 * v0M7.3 does not yet handle:
 *   - Fort assault-capture (that lives in the siege pipeline —
 *     v0M7.4d wiring).
 *   - Auto-mint of Road/Bridge as a build order completes (this DOES
 *     happen — see `spec.buildTimeDays > 0` branch — but the effect
 *     wiring (movement multiplier / river-cost override) lands in
 *     v0M7.4b/c).
 */

import type { Command } from './commands';
import type { GameState, WorldStructureId, WorldStructureKind, ArmyId } from './state';
import type { RngState } from './rng';
import type { SimContext } from './sim-context';
import {
  sortedIds,
  TICKS_PER_GAME_DAY,
  createWorldStructure,
  toWorldStructureId,
} from './state';
import { loadMapStructures, type MapStructureSpec } from '../data/map-structures';
import { WORLD_BUILD_ARRIVAL_EPSILON, ROAD_BUILD_TIME_PER_CELL_DAYS, WORLD_BRIDGE_MOVE_MULTIPLIER } from './apply-commands';
import { hierarchicalPath } from './pathfind';

/**
 * Cached MapStructures roster. Loaded once at module init. Read via
 * `getSpec` which normalises WorldStructureKind ('watchtower') to xlsx
 * row name ('Watch Tower') per the mapping below.
 */
const MAP_STRUCTURES = loadMapStructures();

/**
 * WorldStructureKind → xlsx row name. Kept in sync with the
 * MapStructures sheet in `docs/Balance.xlsx` (v0M7.0). Road + Bridge
 * are not authored as rows at v1 (they're single-effect and dial-only);
 * their entries map to '' so `getSpec` returns undefined for them.
 */
const XLSX_NAME_BY_KIND: Readonly<Record<WorldStructureKind, string>> = {
  watchtower: 'Watch Tower',
  fort: 'Fort',
  road: '',
  bridge: '',
};

/** Return the MapStructureSpec for a kind, or undefined for kinds
 *  without an xlsx row (Road / Bridge at v1) OR when the sheet is
 *  absent. Callers must handle undefined per-kind. */
function getSpec(kind: WorldStructureKind): MapStructureSpec | undefined {
  const name = XLSX_NAME_BY_KIND[kind];
  if (!name) return undefined;
  return MAP_STRUCTURES[name];
}

/**
 * v0M7.3 build-time fallbacks for kinds that don't have MapStructures
 * rows (Road + Bridge). Playtest-tunable at a follow-up if these two
 * warrant per-row state.
 */
const BUILD_TIME_DAYS_FALLBACK: Readonly<Record<WorldStructureKind, number>> = {
  road: 10,
  bridge: 30,
  watchtower: 20,  // Only used if MapStructures sheet is absent.
  fort: 60,        // Only used if MapStructures sheet is absent.
};

/** Effective build-time for a kind (spec if authored, else fallback).
 *  v0M7.5b (2026-07-26) — for `road` the timing is per-cell in a
 *  polyline chain, driven by `ROAD_BUILD_TIME_PER_CELL_DAYS`. All
 *  other kinds still use the MapStructures spec / hardcoded fallback. */
function buildTimeDaysFor(kind: WorldStructureKind): number {
  if (kind === 'road') return ROAD_BUILD_TIME_PER_CELL_DAYS;
  return getSpec(kind)?.buildTimeDays ?? BUILD_TIME_DAYS_FALLBACK[kind];
}

/** Effective capture-days for a kind (0 if no capture — Fort, Road, Bridge). */
function captureDaysFor(kind: WorldStructureKind): number {
  return getSpec(kind)?.captureDays ?? 0;
}

// ---------------------------------------------------------------------------
// buildSystem
// ---------------------------------------------------------------------------

/**
 * v0M7.4c (2026-07-25) — Build the bridge-cell overlay set that
 * pathfind consumes. Returns a Set of cellIndex (`y × width + x`) for
 * every Bridge WorldStructure in the state. Empty set (or undefined
 * upstream) means "no bridges", which lets pathfind take the fast
 * path with zero overhead. Callers pass this to `hierarchicalPath`
 * alongside `WORLD_BRIDGE_MOVE_MULTIPLIER` so the pathfinder treats
 * bridged River cells as passable at the multiplier cost.
 *
 * O(numWorldStructures). Trivially cheap at v0M7 scales (few bridges).
 */
export function computeBridgeCells(state: GameState, mapWidth: number): ReadonlySet<number> | undefined {
  if (!state.worldStructures || state.worldStructures.size === 0) return undefined;
  let cells: Set<number> | undefined;
  for (const ws of state.worldStructures.values()) {
    if (ws.kind !== 'bridge') continue;
    if (!cells) cells = new Set();
    cells.add(ws.cell.y * mapWidth + ws.cell.x);
  }
  return cells;
}

export function buildSystem(
  state: GameState,
  _commands: readonly Command[],
  _rng: RngState,
  context: SimContext,
): GameState {
  const dayIncrement = 1 / TICKS_PER_GAME_DAY;
  const completions: Array<{ armyId: ArmyId; kind: WorldStructureKind; cell: { x: number; y: number }; ownerFactionId: import('./state').FactionId }> = [];

  for (const armyId of sortedIds(state.armies)) {
    const army = state.armies.get(armyId);
    if (!army || !army.buildOrder) continue;
    // Battle pauses accrual — army is committed elsewhere.
    if (army.currentBattleId !== null) continue;

    const target = army.buildOrder.targetCell;
    // MFix11.4 (2026-07-27) — floor-based cell-match check (matches
    // captureSystem semantics below). Pre-fix used a distance-to-centre
    // check against (target.x + 0.5, target.y + 0.5) with epsilon 0.4,
    // but waypoints from the pathfinder are integer cell coordinates so
    // armies arrived at (target.x, target.y) exactly — distance to
    // centre = sqrt(0.5) ≈ 0.707, exceeding epsilon → progress never
    // accrued. WORLD_BUILD_ARRIVAL_EPSILON stays reserved for future
    // sub-cell precision work; today the check is a pure occupancy match.
    const ax = Math.floor(army.position.x);
    const ay = Math.floor(army.position.y);
    if (ax !== target.x || ay !== target.y) {
      // Off-cell → reset progress. Order persists.
      army.buildOrder.progressDays = 0;
      continue;
    }
    // Epsilon still consulted to suppress an unused-import diagnostic +
    // as an explicit design marker; a future sub-cell precision pass
    // may re-enable a proper distance check.
    void WORLD_BUILD_ARRIVAL_EPSILON;
    // On-cell → accrue.
    army.buildOrder.progressDays += dayIncrement;
    const threshold = buildTimeDaysFor(army.buildOrder.kind);
    if (army.buildOrder.progressDays >= threshold) {
      completions.push({
        armyId,
        kind: army.buildOrder.kind,
        cell: { x: target.x, y: target.y },
        ownerFactionId: army.ownerFactionId,
      });
    }
  }

  // Apply completions after iteration.
  for (const c of completions) {
    // Road is unowned per MAP.md §9.2; every other kind belongs to builder.
    const owner = c.kind === 'road' ? null : c.ownerFactionId;
    if (state.nextWorldStructureId === undefined || state.nextWorldStructureId < 1) {
      state.nextWorldStructureId = 1;
    }
    const id = toWorldStructureId(state.nextWorldStructureId);
    state.nextWorldStructureId += 1;
    const ws = createWorldStructure(id, c.kind, owner, c.cell);
    if (!state.worldStructures) state.worldStructures = new Map();
    state.worldStructures.set(id, ws);

    // v0M7.5b (2026-07-26) — Road polyline chaining. If the completed
    // build has more cells in `roadPath`, pop the next cell + set it
    // as the new targetCell + reset progressDays + auto-plot a
    // plannedPath so the Army walks there. Otherwise clear the order.
    const army = state.armies.get(c.armyId);
    if (!army) continue;
    if (army.buildOrder && army.buildOrder.roadPath && army.buildOrder.roadPath.length > 0) {
      const next = army.buildOrder.roadPath[0]!;
      const remaining = army.buildOrder.roadPath.slice(1);
      army.buildOrder = {
        kind: army.buildOrder.kind,
        targetCell: { x: next.x, y: next.y },
        progressDays: 0,
        ...(remaining.length > 0 ? { roadPath: remaining } : {}),
      };
      // Plot movement toward the new cell so the Army walks there
      // without player intervention. Pathfind honours current bridges.
      const startX = Math.round(army.position.x);
      const startY = Math.round(army.position.y);
      if (startX !== next.x || startY !== next.y) {
        const path = hierarchicalPath(
          context.terrain,
          context.regionGraph,
          startX,
          startY,
          next.x,
          next.y,
          computeBridgeCells(state, context.terrain.width),
          WORLD_BRIDGE_MOVE_MULTIPLIER,
        );
        if (path.found) {
          const smoothed = path.smoothedCells;
          const waypoints = smoothed.length > 1 ? smoothed.slice(1) : smoothed.slice();
          army.plannedPath = waypoints;
          army.movementGoal = { x: next.x, y: next.y };
        }
      }
    } else {
      // Order complete (single-cell buildable OR polyline final cell).
      army.buildOrder = null;
    }
  }

  return state;
}

// ---------------------------------------------------------------------------
// captureSystem
// ---------------------------------------------------------------------------

export function captureSystem(
  state: GameState,
  _commands: readonly Command[],
  _rng: RngState,
  _context: SimContext,
): GameState {
  if (!state.worldStructures || state.worldStructures.size === 0) return state;
  // Day-boundary gate — capture ticks at day resolution to match design lock.
  if (state.tick % TICKS_PER_GAME_DAY !== 0) return state;

  const flipsToApply: Array<{ id: WorldStructureId; newOwner: import('./state').FactionId }> = [];
  const destroysToApply: WorldStructureId[] = [];

  for (const wsId of sortedIds(state.worldStructures)) {
    const ws = state.worldStructures.get(wsId as WorldStructureId);
    if (!ws) continue;
    const captureDays = captureDaysFor(ws.kind);
    if (captureDays <= 0) continue; // Kind is not park-capturable (Fort, Road, Bridge).

    // Find any Army parked on this cell.
    let enemyAttackerFactionId: import('./state').FactionId | null = null;
    for (const armyId of sortedIds(state.armies)) {
      const army = state.armies.get(armyId);
      if (!army) continue;
      const ax = Math.floor(army.position.x);
      const ay = Math.floor(army.position.y);
      if (ax !== ws.cell.x || ay !== ws.cell.y) continue;
      // Own-tower Army does nothing (defenders don't capture their own).
      if (army.ownerFactionId === ws.ownerFactionId) continue;
      // First enemy Army wins the contester slot (deterministic via sortedIds).
      enemyAttackerFactionId = army.ownerFactionId;
      break;
    }

    if (enemyAttackerFactionId === null) {
      // No enemy present → clear any in-flight capture.
      if (ws.captureProgress) ws.captureProgress = null;
      continue;
    }

    // Enemy present. Start / continue / reset the capture attempt.
    if (!ws.captureProgress) {
      ws.captureProgress = { byFactionId: enemyAttackerFactionId, days: 1, mode: 'capture' };
    } else if (ws.captureProgress.byFactionId !== enemyAttackerFactionId) {
      // Different attacker took the cell — reset the timer under new attribution.
      ws.captureProgress = { byFactionId: enemyAttackerFactionId, days: 1, mode: 'capture' };
    } else {
      ws.captureProgress.days += 1;
    }

    if (ws.captureProgress.days >= captureDays) {
      if (ws.captureProgress.mode === 'destroy') {
        destroysToApply.push(ws.id);
      } else {
        flipsToApply.push({ id: ws.id, newOwner: enemyAttackerFactionId });
      }
    }
  }

  // Apply resolutions after iteration.
  for (const flip of flipsToApply) {
    const ws = state.worldStructures.get(flip.id);
    if (!ws) continue;
    ws.ownerFactionId = flip.newOwner;
    ws.captureProgress = null;
  }
  for (const id of destroysToApply) {
    state.worldStructures.delete(id);
  }

  return state;
}
